import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  Download,
  FileText,
  Loader2,
  Search,
  Printer,
  ScrollText,
  CalendarDays,
  BookOpen,
  ListOrdered,
  ChevronDown,
  Link2,
  MessageCircle,
  Check,
  ArrowLeftRight,
  Sparkles,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const toAscii = (s) =>
  (s || "").replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString());

// Amendment reference (renders in red).
const AMEND_RE =
  /((?:مستبدل\S*|مُستبدل\S*|معدّ?ل\S*|مُعدّ?ل\S*|مضاف\S*|مُضاف\S*|ملغا\S*|مُلغا\S*|أُلغيت|ألغيت|استبدل\S*|استُبدل\S*|عدّل\S*|عُدّل\S*|أضيف\S*|أُضيف\S*)[^\n)]{0,60}?(?:بموجب\s+)?(?:بالقانون|بالمرسوم|بمرسوم|بقانون|القانون|المرسوم)\s*(?:بقانون\s*)?رقم\s*\(?\s*[\d٠-٩]+\s*\)?\s*لسنة\s*[\d٠-٩]+)/g;

// Article header at the start of a block: "المادة (12)" / "مادة ١٢".
const ARTICLE_HEAD_RE = /^\(?\s*(?:ال)?ماد[ةه]\s*\(?\s*([\d٠-٩]+)/;
// Full article-header token incl. optional closing paren, so we can split
// «مادة (12)» off as a bold heading and drop its body onto the next line.
const HEAD_LABEL_RE = /^\(?\s*(?:ال)?ماد[ةه]\s*\(?\s*[\d٠-٩]+\s*\)?/;
// In-text reference to an article, for linkifying.
const ARTICLE_REF_RE = /((?:ال)?ماد[ةه]\s*\(?\s*[\d٠-٩]+\s*\)?)/g;
// In-text reference to ANOTHER law, e.g. "القانون رقم 5 لسنة 2010".
const LAW_REF_RE =
  /((?:مرسوم\s+بقانون|بقانون|القانون|قانون)\s*(?:رقم\s*)?\(?\s*[\d٠-٩]+\s*\)?\s*لسنة\s*[\d٠-٩]+)/g;
const LAW_NUM_YEAR_RE = /(?:رقم\s*)?\(?\s*([\d٠-٩]+)\s*\)?\s*لسنة\s*([\d٠-٩]+)/;

// Word-level LCS diff between the before/after amendment text so changed
// words can be highlighted. Capped to keep the DP cheap.
const diffWords = (aStr, bStr) => {
  const a = (aStr || "").trim().split(/\s+/).slice(0, 400);
  const b = (bStr || "").trim().split(/\s+/).slice(0, 400);
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const aOut = [], bOut = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { aOut.push({ w: a[i], t: "same" }); bOut.push({ w: b[j], t: "same" }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { aOut.push({ w: a[i], t: "del" }); i++; }
    else { bOut.push({ w: b[j], t: "add" }); j++; }
  }
  while (i < n) { aOut.push({ w: a[i], t: "del" }); i++; }
  while (j < m) { bOut.push({ w: b[j], t: "add" }); j++; }
  return { aOut, bOut };
};

const renderDiff = (tokens) =>
  tokens.map((tk, i) => (
    <React.Fragment key={i}>
      <span
        style={
          tk.t === "del"
            ? { background: "rgba(185,28,28,0.20)", textDecoration: "line-through", borderRadius: 2, padding: "0 2px" }
            : tk.t === "add"
            ? { background: "rgba(5,150,105,0.24)", fontWeight: 700, borderRadius: 2, padding: "0 2px" }
            : undefined
        }
      >
        {tk.w}
      </span>{" "}
    </React.Fragment>
  ));

export default function LawDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const { user } = useAuth();
  const isAr = lang === "ar";
  const [copiedArt, setCopiedArt] = useState(null);
  const [fav, setFav] = useState(false);

  const LAW_FAV_KEY = "aldakheel_fav_laws";
  useEffect(() => {
    try {
      setFav(JSON.parse(localStorage.getItem(LAW_FAV_KEY) || "[]").includes(id));
    } catch { /* noop */ }
  }, [id]);

  const toggleFav = () => {
    let arr = [];
    try { arr = JSON.parse(localStorage.getItem(LAW_FAV_KEY) || "[]"); } catch { arr = []; }
    if (arr.includes(id)) {
      arr = arr.filter((x) => x !== id);
      setFav(false);
      toast.success(isAr ? "أُزيل من المحفوظات" : "Removed from saved");
    } else {
      arr.push(id);
      setFav(true);
      toast.success(isAr ? "أُضيف إلى محفوظاتي" : "Saved");
    }
    localStorage.setItem(LAW_FAV_KEY, JSON.stringify(arr));
  };

  const askSamiLaw = () => {
    if (!law) return;
    const q = isAr
      ? `بخصوص «${law.title}»${law.law_year ? ` (سنة ${law.law_year})` : ""}. اشرح لي أحكام هذا ${law.document_kind === "regulation" ? "اللائحة/القرار" : "القانون"} وما ينطبق منه، مع الإشارة للمواد ذات الصلة.`
      : `Regarding "${law.title}". Explain its provisions and what applies, citing relevant articles.`;
    navigate(`/consultant?q=${encodeURIComponent(q)}&ask=1`);
  };

  const [law, setLaw] = useState(null);
  const [err, setErr] = useState(false);
  const [query, setQuery] = useState("");
  const [flashId, setFlashId] = useState(null);
  const [flashSeq, setFlashSeq] = useState(null);
  const [showMemo, setShowMemo] = useState(false);
  const [lawIndex, setLawIndex] = useState([]);
  const [indexOpen, setIndexOpen] = useState(true);
  const [amendments, setAmendments] = useState([]);
  const [artIdx, setArtIdx] = useState(null);
  const [showCompare, setShowCompare] = useState(true);
  const [summaries, setSummaries] = useState({});
  const [summarizing, setSummarizing] = useState(false);

  useEffect(() => {
    setLaw(null);
    setErr(false);
    setShowMemo(false);
    api
      .get(`/laws/${id}`)
      .then((r) => setLaw(r.data))
      .catch(() => setErr(true));
  }, [id]);

  useEffect(() => {
    api
      .get("/law-index")
      .then((r) => setLawIndex(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setAmendments([]);
    api
      .get(`/laws/${id}/amendments`)
      .then((r) => setAmendments(r.data?.items || []))
      .catch(() => {});
  }, [id]);

  // Server-side article index: repaired numbering + guide headings (if published).
  useEffect(() => {
    setArtIdx(null);
    api
      .get(`/laws/${id}/article-index`)
      .then((r) => setArtIdx(r.data || null))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!amendments.length) return;
    const initial = {};
    amendments.forEach((a) => {
      if (a.summary) initial[a.key] = a.summary;
    });
    setSummaries(initial);
    // Generating + caching summaries is an admin-only content write. Regular
    // users just display whatever is already cached (from the GET above).
    if (user?.role !== "admin") return;
    const missing = amendments.some((a) => a.has_after && a.key && !a.summary);
    if (!missing) return;
    setSummarizing(true);
    api
      .post(`/laws/${id}/amendments/summarize`)
      .then((r) => setSummaries((prev) => ({ ...prev, ...(r.data?.summaries || {}) })))
      .catch(() => {})
      .finally(() => setSummarizing(false));
  }, [amendments, id, user]);

  const t = useMemo(
    () => ({
      back: isAr ? "← مكتبة القوانين" : "← Laws library",
      eyebrow: isAr ? "نص قانوني" : "Statute",
      brandedPdf: isAr ? "تحميل PDF" : "Download PDF",
      word: isAr ? "نسخة Word" : "Word",
      original: isAr ? "الملف الأصلي" : "Original PDF",
      print: isAr ? "طباعة" : "Print",
      memoBtn: isAr ? "المذكرة التفسيرية" : "Explanatory memo",
      search: isAr ? "ابحث داخل هذا القانون..." : "Search inside this law…",
      notFound: isAr ? "لم يُعثر على هذا القانون." : "Law not found.",
      memoTitle: isAr ? "المذكرة التفسيرية" : "Explanatory memorandum",
      memoHide: isAr ? "إخفاء المذكرة" : "Hide memorandum",
      memoShow: isAr ? "عرض المذكرة التفسيرية" : "Show explanatory memorandum",
      memoHint: isAr
        ? "شرح رسمي لأحكام القانون. اضغط على أي «مادة» للانتقال إلى نصها."
        : "Official explanation of the law. Tap any «article» to jump to its text.",
      chars: (n) => (isAr ? `${n.toLocaleString("ar")} حرف` : `${n.toLocaleString()} chars`),
      articles: (n) => (isAr ? `${n} مادة` : `${n} articles`),
      disclaimer: isAr
        ? "هذه الوثيقة معدّة للاطلاع فقط ولا تغني عن النص المنشور في الجريدة الرسمية."
        : "Reference copy only. Always verify against the official gazette.",
      compareTitle: isAr ? "مقارنة التعديلات" : "Amendment comparison",
      compareHint: isAr
        ? "النص كما ورد رسمياً «قبل التعديل» مقابل «بعد التعديل»، كما توثّقه الوثيقة الرسمية لهذا القانون."
        : "Official «before» vs «after» text for each amendment, as documented in this law's official source.",
      cmpBefore: isAr ? "قبل التعديل" : "Before amendment",
      cmpAfter: isAr ? "بعد التعديل" : "After amendment",
      cmpShow: isAr ? "عرض" : "Show",
      cmpHide: isAr ? "إخفاء" : "Hide",
      cmpCurrent: isAr
        ? "النص الحالي معروض في متن القانون أعلاه."
        : "Current text is shown in the law body above.",
      aiChanged: isAr ? "ما الذي تغيّر:" : "What changed:",
      aiLoading: isAr ? "جارٍ تلخيص التغيير بالذكاء…" : "Summarising the change with AI…",
    }),
    [isAr]
  );

  // Set of article numbers that exist as headers in the body (anchor targets).
  const articleSet = useMemo(() => {
    const s = new Set();
    if (!law?.text) return s;
    law.text
      .split(/\n+/)
      .map((x) => x.trim())
      .forEach((line) => {
        const m = line.match(ARTICLE_HEAD_RE);
        if (m && line.length < 120) s.add(toAscii(m[1]));
      });
    return s;
  }, [law]);

  // Map "num-year" → published law, for in-text cross-linking.
  const lawIndexMap = useMemo(() => {
    const m = new Map();
    (lawIndex || []).forEach((l) => {
      let num = l.law_number != null ? String(l.law_number) : null;
      let year = l.law_year != null ? String(l.law_year) : null;
      if (num == null || year == null) {
        const tm = (l.title || "").match(LAW_NUM_YEAR_RE);
        if (tm) {
          num = tm[1];
          year = tm[2];
        }
      }
      if (num != null && year != null) {
        const key = `${toAscii(num)}-${toAscii(year)}`;
        if (!m.has(key)) m.set(key, { id: l.id, title: l.title });
      }
    });
    return m;
  }, [lawIndex]);

  // Ordered list of numbered articles for the side navigation index, each
  // with a short topic/subject snippet. Uses occurrence order (seq) so the
  // index stays correct even when OCR garbled/duplicated the printed numbers.
  const articlesList = useMemo(() => {
    const out = [];
    if (!law?.text) return out;
    const blocks = law.text.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    let seq = 0;
    blocks.forEach((line, idx) => {
      const m = line.match(ARTICLE_HEAD_RE);
      if (!m || line.length >= 200) return;
      seq += 1;
      const num = toAscii(m[1]);
      // Topic = remainder of the header line after "مادة (X)", else the
      // opening words of the following block.
      let topic = line.replace(ARTICLE_HEAD_RE, "").replace(/^[\s)\-:.،]+/, "").trim();
      if (topic.length < 4) topic = (blocks[idx + 1] || "").trim();
      topic = topic.replace(/\s+/g, " ").slice(0, 70);
      out.push({ seq, num, topic });
    });
    return out;
  }, [law]);

  // Whether the printed article numbers look trustworthy (strictly rising,
  // few zeros/duplicates). When false, the index labels articles by their
  // sequential order instead of the garbled OCR number.
  const numberingReliable = useMemo(() => {
    const nums = articlesList.map((a) => Number(a.num)).filter((n) => !Number.isNaN(n));
    if (nums.length < 3) return true;
    let rising = 0;
    for (let i = 1; i < nums.length; i++) if (nums[i] > nums[i - 1]) rising += 1;
    const dups = nums.length - new Set(nums).size;
    const zeros = nums.filter((n) => n === 0).length;
    return rising / (nums.length - 1) >= 0.7 && (dups + zeros) / nums.length < 0.2;
  }, [articlesList]);

  // Structural outline: chapters/sections (كتاب/باب/فصل/فرع...) with the
  // article-number range each spans, so the right-hand index mirrors the
  // law's table of contents and jumps to the article text on click.
  const lawOutline = useMemo(() => {
    if (!law?.text) return [];
    const blocks = law.text.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    const KW = { "الكتاب": 0, "الجزء": 0, "الباب": 1, "الفصل": 2, "الفرع": 3, "القسم": 3, "المبحث": 3, "الفقرة": 3 };
    const headRe = /^(الكتاب|الجزء|الباب|الفصل|الفرع|القسم|المبحث|الفقرة)[\s:]/;
    const heads = [];
    const arts = [];
    blocks.forEach((line, idx) => {
      const hm = line.match(headRe);
      if (hm && line.length < 120) {
        heads.push({ idx, level: KW[hm[1]] ?? 3, title: line.replace(/\s+/g, " ").trim() });
        return;
      }
      const am = line.match(ARTICLE_HEAD_RE);
      if (am && line.length < 120) {
        const n = Number(toAscii(am[1]));
        if (!Number.isNaN(n)) arts.push({ idx, n });
      }
    });
    if (heads.length < 2) return [];
    return heads.map((h, i) => {
      const nextIdx = i + 1 < heads.length ? heads[i + 1].idx : Infinity;
      const nums = arts.filter((a) => a.idx > h.idx && a.idx < nextIdx).map((a) => a.n);
      const from = nums.length ? Math.min(...nums) : null;
      const to = nums.length ? Math.max(...nums) : null;
      return { level: h.level, title: h.title, from, to };
    });
  }, [law]);

  // Unified, document-order index: chapter headings AND each article with its
  // topic/subject name. `seq` matches the art-seq-<n> anchors in renderText.
  const tocEntries = useMemo(() => {
    if (!law?.text) return [];
    const blocks = law.text.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    const KW = { "الكتاب": 0, "الجزء": 0, "الباب": 1, "الفصل": 2, "الفرع": 3, "القسم": 3, "المبحث": 3, "الفقرة": 3 };
    const headRe = /^(الكتاب|الجزء|الباب|الفصل|الفرع|القسم|المبحث|الفقرة)[\s:]/;
    const out = [];
    let seq = 0;
    blocks.forEach((line, idx) => {
      const am = line.match(ARTICLE_HEAD_RE);
      if (am && line.length < 200) {
        seq += 1;
        const num = toAscii(am[1]);
        let topic = line.replace(ARTICLE_HEAD_RE, "").replace(/^[\s)\-:.،]+/, "").trim();
        if (topic.length < 4) topic = (blocks[idx + 1] || "").trim();
        topic = topic.replace(/\s+/g, " ").slice(0, 90);
        out.push({ type: "article", seq, num, topic });
        return;
      }
      const hm = line.match(headRe);
      if (hm && line.length < 120) {
        out.push({ type: "chapter", level: KW[hm[1]] ?? 3, title: line.replace(/\s+/g, " ").trim() });
      }
    });
    // Merge the server index only when it describes exactly the same articles.
    if (artIdx && Array.isArray(artIdx.entries) && artIdx.count === seq) {
      const bySeq = new Map(artIdx.entries.map((x) => [x.seq, x]));
      out.forEach((o) => {
        if (o.type !== "article") return;
        const x = bySeq.get(o.seq);
        if (!x) return;
        o.num = String(x.num);
        o.flag = x.flag;
        o.reliable = artIdx.needs_review ? false : true;
        if (x.topic) {
          o.topic = x.topic;
          o.guide = true;
        }
      });
    }
    return out;
  }, [law, artIdx]);

  // Prefer the structural outline only when it's reasonably detailed; for
  // laws with few chapter headers but many articles (OCR often misses the
  // chapter lines), fall back to the richer flat article list so the index
  // stays useful.
  const goToArticle = (n) => {
    const el = document.getElementById(`article-${n}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlashId(n);
    window.setTimeout(() => setFlashId((cur) => (cur === n ? null : cur)), 1800);
  };

  // Navigate by occurrence sequence (unambiguous even with duplicate numbers).
  const goToSeq = (seq) => {
    const el = document.getElementById(`art-seq-${seq}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlashSeq(seq);
    window.setTimeout(() => setFlashSeq((cur) => (cur === seq ? null : cur)), 1800);
  };

  // Direct shareable deep-link to a specific article.
  const articleLink = (n) => `${window.location.origin}/laws/${id}#article-${n}`;
  const waShareUrl = (n) =>
    `https://wa.me/?text=${encodeURIComponent(
      `${law?.title || ""} — ${isAr ? "المادة" : "Article"} ${n}\n${articleLink(n)}`
    )}`;
  const copyArticleLink = (n) => {
    const url = articleLink(n);
    const done = () => {
      setCopiedArt(n);
      toast.success(isAr ? "تم نسخ رابط المادة" : "Article link copied");
      window.setTimeout(() => setCopiedArt((c) => (c === n ? null : c)), 1600);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(done);
    } else {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
      done();
    }
  };

  // Deep-link support: opening /laws/:id#article-N (e.g. from AI search)
  // scrolls straight to the matched article once the text is rendered.
  useEffect(() => {
    if (!law?.text) return;
    const h = (location.hash || "").match(/article-([\d٠-٩]+)/);
    if (!h) return;
    const num = toAscii(h[1]);
    const timer = window.setTimeout(() => goToArticle(num), 450);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [law, location.hash]);

  // Query highlighter → returns react nodes for a plain string.
  const highlightQuery = (text, keyPrefix) => {
    const q = (query || "").trim();
    if (!q) return [<React.Fragment key={`${keyPrefix}-p`}>{text}</React.Fragment>];
    const qre = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    return text.split(qre).map((p, j) =>
      j % 2 === 1 ? (
        <mark
          key={`${keyPrefix}-m-${j}`}
          className="px-0.5 rounded-sm"
          style={{ background: "#FFF59D", color: "#1a1a1a" }}
        >
          {p}
        </mark>
      ) : (
        <React.Fragment key={`${keyPrefix}-t-${j}`}>{p}</React.Fragment>
      )
    );
  };

  // Linkify in-text article references AND cross-law references, then
  // highlight the remaining plain text.
  const renderTokens = (text, keyPrefix, { linkArticles = false, linkLaws = false } = {}) => {
    if (!linkArticles && !linkLaws) return highlightQuery(text, keyPrefix);
    const srcs = [];
    if (linkLaws) srcs.push(LAW_REF_RE.source);
    if (linkArticles) srcs.push(ARTICLE_REF_RE.source);
    const master = new RegExp(srcs.join("|"), "g");
    const nodes = [];
    let last = 0;
    let m;
    let i = 0;
    while ((m = master.exec(text))) {
      const phrase = m[0];
      if (m.index > last) {
        nodes.push(...highlightQuery(text.slice(last, m.index), `${keyPrefix}-${i}a`));
      }
      let node = null;
      if (linkLaws && /قانون/.test(phrase) && /لسنة/.test(phrase)) {
        const lm = phrase.match(LAW_NUM_YEAR_RE);
        if (lm) {
          const num = toAscii(lm[1]);
          const year = toAscii(lm[2]);
          const found = lawIndexMap.get(`${num}-${year}`);
          if (found && found.id !== id) {
            node = (
              <Link
                key={`${keyPrefix}-${i}-law`}
                to={`/laws/${found.id}`}
                data-testid={`law-ref-link-${num}-${year}`}
                title={found.title}
                className="underline underline-offset-2 decoration-dotted hover:decoration-solid font-medium"
                style={{ color: "var(--navy)" }}
              >
                {phrase}
              </Link>
            );
          }
        }
      }
      if (!node && linkArticles && /ماد[ةه]/.test(phrase)) {
        const am = phrase.match(/[\d٠-٩]+/);
        const num = am ? toAscii(am[0]) : null;
        if (num && articleSet.has(num)) {
          node = (
            <button
              key={`${keyPrefix}-${i}-art`}
              type="button"
              onClick={() => goToArticle(num)}
              data-testid={`law-article-link-${num}`}
              className="underline underline-offset-2 decoration-dotted hover:decoration-solid font-medium"
              style={{ color: "var(--gold-deep)" }}
            >
              {phrase}
            </button>
          );
        }
      }
      if (node) nodes.push(node);
      else nodes.push(...highlightQuery(phrase, `${keyPrefix}-${i}p`));
      last = m.index + phrase.length;
      i += 1;
    }
    if (last < text.length) {
      nodes.push(...highlightQuery(text.slice(last), `${keyPrefix}-tail`));
    }
    return nodes;
  };

  // Render one block: split amendment refs (red) from linkable normal text.
  const renderInline = (text, keyPrefix, { linkArticles = false, linkLaws = false } = {}) => {
    const segs = text.split(AMEND_RE);
    return segs.map((seg, i) => {
      if (i % 2 === 1) {
        return (
          <span key={`${keyPrefix}-am-${i}`} className="font-semibold" style={{ color: "#b91c1c" }}>
            {seg}
          </span>
        );
      }
      return (
        <React.Fragment key={`${keyPrefix}-n-${i}`}>
          {renderTokens(seg, `${keyPrefix}-n-${i}`, { linkArticles, linkLaws })}
        </React.Fragment>
      );
    });
  };

  // Render a full text body. assignIds=true stamps article anchors + skips
  // linkifying article headers (they are the targets).
  const renderText = (raw, { assignIds = true } = {}) => {
    if (!raw) return null;
    const blocks = raw.split(/\n+/).map((s) => s.trim()).filter(Boolean);

    const markerSet = new Set();
    blocks.forEach((b, i) => {
      if (b.match(AMEND_RE)) markerSet.add(i);
    });
    const amendedSet = new Set();
    markerSet.forEach((i) => {
      amendedSet.add(i);
      if (i > 0) amendedSet.add(i - 1);
    });

    let artSeqCtr = 0;
    const firstNumSeen = new Set();

    return blocks.map((text, key) => {
      const headMatch = text.match(HEAD_LABEL_RE);
      const isArticle = !!headMatch && text.length < 200;
      const label = isArticle ? headMatch[0].replace(/\s+/g, " ").trim() : null;
      const remainder = isArticle
        ? text.slice(headMatch[0].length).replace(/^[\s:.\-)،]+/, "").trim()
        : text;
      const artNum = isArticle ? toAscii((headMatch[0].match(/[\d٠-٩]+/) || [""])[0]) : null;
      const artSeq = isArticle ? ++artSeqCtr : null;
      const firstOfNum = isArticle && !firstNumSeen.has(artNum);
      if (isArticle) firstNumSeen.add(artNum);
      const amended = amendedSet.has(key);
      const amend = markerSet.has(key) ? text.match(AMEND_RE) : null;
      const flashing = isArticle && (flashSeq === artSeq || flashId === artNum);
      const bodyKey = `${assignIds ? "b" : "m"}-${key}`;

      return (
        <React.Fragment key={bodyKey}>
          {isArticle ? (
            <>
              {assignIds && firstOfNum && (
                <span id={`article-${artNum}`} className="block scroll-mt-24" aria-hidden="true" />
              )}
              <p
                id={assignIds ? `art-seq-${artSeq}` : undefined}
                data-testid={`law-article-head-${artNum}`}
                dir="rtl"
                className={
                  "font-display font-bold text-lg lg:text-xl mt-8 pt-5 mb-2 scroll-mt-24 border-t group flex items-center justify-between gap-3" +
                  (flashing ? " rounded-md px-2 -mx-2 transition-colors" : "")
                }
                style={{
                  color: "var(--gold-deep)",
                  background: flashing ? "rgba(255,235,59,0.55)" : undefined,
                  borderColor: "var(--line)",
                }}
              >
                <span className="min-w-0">{label}</span>
                {assignIds && (
                  <span className="shrink-0 flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity print:hidden">
                    <button
                      type="button"
                      onClick={() => copyArticleLink(artNum)}
                      data-testid={`law-article-copy-${artNum}`}
                      title={isAr ? "نسخ رابط المادة" : "Copy article link"}
                      aria-label={isAr ? "نسخ رابط المادة" : "Copy article link"}
                      className="p-1.5 rounded-md hover:bg-[var(--beige)] transition-colors"
                      style={{ color: copiedArt === artNum ? "#059669" : "var(--gold-deep)" }}
                    >
                      {copiedArt === artNum ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                    </button>
                    <a
                      href={waShareUrl(artNum)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`law-article-wa-${artNum}`}
                      title={isAr ? "مشاركة على واتساب" : "Share on WhatsApp"}
                      aria-label={isAr ? "مشاركة على واتساب" : "Share on WhatsApp"}
                      className="p-1.5 rounded-md hover:bg-[var(--beige)] transition-colors"
                      style={{ color: "#25D366" }}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  </span>
                )}
              </p>
              {remainder && (
                <p
                  data-testid={amended ? `law-amended-paragraph-${key}` : `law-paragraph-${key}`}
                  dir="rtl"
                  className="leading-[2.05] mb-3 text-[15px] lg:text-base"
                  style={{ color: amended ? "#b91c1c" : "var(--ink)" }}
                >
                  {renderInline(remainder, bodyKey, { linkArticles: true, linkLaws: true })}
                </p>
              )}
            </>
          ) : (
            <p
              data-testid={amended ? `law-amended-paragraph-${key}` : `law-paragraph-${key}`}
              dir="rtl"
              className={
                "leading-[2.05] mb-3 text-[15px] lg:text-base" +
                (flashing ? " rounded-md px-2 -mx-2 transition-colors" : "")
              }
              style={{
                color: amended ? "#b91c1c" : "var(--ink)",
                background: flashing ? "rgba(255,235,59,0.55)" : undefined,
              }}
            >
              {renderInline(text, bodyKey, { linkArticles: true, linkLaws: true })}
            </p>
          )}
          {amend && (
            <p
              data-testid={`law-amendment-note-${key}`}
              dir="rtl"
              className="mb-4 -mt-0.5 text-[13px] lg:text-sm rounded-md px-3 py-2 border-r-4"
              style={{ color: "#b91c1c", background: "rgba(185,28,28,0.06)", borderColor: "#b91c1c" }}
            >
              <span className="font-semibold">⚖ نص معدَّل — </span>
              عُدِّل هذا النص بموجب: {renderTokens(amend.join("، "), `note-${key}`, { linkLaws: true })}
            </p>
          )}
        </React.Fragment>
      );
    });
  };

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-slate-600">{t.notFound}</p>
        <Link to="/laws" className="inline-block mt-6 underline">
          {t.back}
        </Link>
      </div>
    );
  }

  if (!law) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const hasMemo = !!law.explanatory_memorandum;

  return (
    <div data-testid="law-detail-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
      <Link
        to="/laws"
        data-testid="back-to-laws"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 dark:hover:text-amber-500 transition-colors mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t.back}
      </Link>

      {/* HEADER */}
      <div className="border-b pb-10 mb-10" style={{ borderColor: "var(--line)" }}>
        <p className="eyebrow">{law.category || t.eyebrow}</p>
        <h1 dir="rtl" className="font-display text-3xl lg:text-5xl mt-3 leading-[1.15] tracking-tight">
          {law.title}
        </h1>

        <div className="mt-5 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.18em] text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <ScrollText className="w-3.5 h-3.5" />
            {law.language === "ar" ? "AR" : "EN"}
          </span>
          {law.publication_date ? (
            <span
              data-testid="law-publication-date"
              className="flex items-center gap-1.5 normal-case tracking-normal font-medium"
              style={{ color: "var(--gold-deep)" }}
            >
              <span className="w-1 h-1 rounded-full bg-slate-400" />
              <CalendarDays className="w-3.5 h-3.5" />
              {isAr ? "تاريخ الصدور: " : "Issued: "}
              {law.publication_date}
            </span>
          ) : null}
          {law.article_count ? (
            <span className="flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-slate-400" />
              {t.articles(law.article_count)}
            </span>
          ) : null}
          {law.char_count ? (
            <span className="flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-slate-400" />
              {t.chars(law.char_count)}
            </span>
          ) : null}
        </div>

        {/* ACTIONS */}
        <div className="mt-7 flex flex-wrap gap-3 justify-end">
          <button
            type="button"
            onClick={askSamiLaw}
            data-testid="law-ask-sami"
            className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm transition-colors hover:opacity-90"
            style={{ background: "var(--gold-deep)", color: "#fff" }}
          >
            <Sparkles className="w-4 h-4" />
            {isAr ? "اسأل سامي عن هذا القانون" : "Ask Sami about this law"}
          </button>
          <button
            type="button"
            onClick={toggleFav}
            data-testid="law-favorite"
            className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm border transition-colors hover:bg-[var(--beige)]"
            style={{ borderColor: "var(--line-strong)", color: fav ? "var(--gold-deep)" : "var(--ink)" }}
          >
            {fav ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            {fav ? (isAr ? "محفوظ" : "Saved") : (isAr ? "حفظ" : "Save")}
          </button>
          {hasMemo && (
            <button
              type="button"
              data-testid="open-memo-btn"
              onClick={() => {
                setShowMemo(true);
                window.setTimeout(() => {
                  document.getElementById("explanatory-memorandum")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }, 60);
              }}
              className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm transition-colors"
              style={{ background: "var(--gold-deep)", color: "#fff" }}
            >
              <BookOpen className="w-4 h-4" />
              {t.memoBtn}
            </button>
          )}
          <a
            href={`${BACKEND}/api/laws/${law.id}/branded-pdf`}
            data-testid="download-branded-pdf"
            className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm transition-colors"
            style={{ background: "var(--navy)", color: "#fff" }}
          >
            <Download className="w-4 h-4" />
            {t.brandedPdf}
          </a>
          <a
            href={`${BACKEND}/api/laws/${law.id}/word`}
            data-testid="download-word"
            className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm border transition-colors hover:bg-[var(--beige)]"
            style={{ borderColor: "var(--line-strong)", color: "var(--ink)" }}
          >
            <FileText className="w-4 h-4" />
            {t.word}
          </a>
          <a
            href={`${BACKEND}/api/laws/${law.id}/download`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-original"
            className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm border transition-colors hover:bg-[var(--beige)]"
            style={{ borderColor: "var(--line-strong)", color: "var(--ink)" }}
          >
            <FileText className="w-4 h-4" />
            {t.original}
          </a>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="print-law"
            className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm border transition-colors hover:bg-[var(--beige)]"
            style={{ borderColor: "var(--line-strong)", color: "var(--ink)" }}
          >
            <Printer className="w-4 h-4" />
            {t.print}
          </button>
        </div>
      </div>

      {/* SEARCH */}
      <div className="mb-8 max-w-xl">
        <div className="relative">
          <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            dir="auto"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.search}
            data-testid="law-search-internal"
            className="rounded-sm h-11 ps-9"
          />
        </div>
      </div>

      {law.status === "repealed" && (
        <div
          data-testid="law-repealed-banner"
          role="note"
          className="mb-8 border border-red-300 bg-red-50 text-red-900 text-sm p-4 leading-relaxed rounded-sm"
          dir="rtl"
        >
          ⚠️ <strong>{isAr ? "تنبيه: هذا التشريع ملغى." : "Notice: this law has been repealed."}</strong>{" "}
          {law.status_note && law.status_note !== "ملغى" ? law.status_note : (isAr ? "يُرجى الرجوع إلى التشريع الساري قبل الاعتماد عليه." : "")}
        </div>
      )}

      {law.text_quality === "needs_review" && (
        <div
          data-testid="law-quality-banner"
          className="mb-8 border border-amber-300 bg-amber-50 text-amber-900 text-sm p-4 leading-relaxed rounded-sm"
          dir={isAr ? "rtl" : "ltr"}
        >
          {isAr ? (
            <>
              ⚠️ <strong>ملاحظة على جودة النص:</strong> هذه الوثيقة مُسحت ضوئياً من مصدر رسمي وقد
              تحتوي على أخطاء طباعية في بعض الفقرات. يُرجى التأكد من النص الأصلي عبر «الملف الأصلي»
              أعلاه أو من الجريدة الرسمية قبل الاعتماد عليها.
            </>
          ) : (
            <>
              ⚠️ <strong>Text-quality notice:</strong> This document was scanned from the official
              source and may contain OCR typos. Please verify against the «Original PDF» above.
            </>
          )}
        </div>
      )}

      {/* CONTENT + ARTICLE INDEX */}
      <div className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-10">
        {articlesList.length > 3 ? (
          <aside
            data-testid="law-article-index"
            className="mb-8 lg:mb-0 lg:sticky lg:top-24 lg:self-start border rounded-sm"
            style={{ borderColor: "var(--line)" }}
          >
            <button
              type="button"
              onClick={() => setIndexOpen((o) => !o)}
              data-testid="toggle-article-index"
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium"
            >
              <span className="flex items-center gap-2">
                <ListOrdered className="w-4 h-4" style={{ color: "var(--gold-deep)" }} />
                {`${isAr ? "الفهرس" : "Index"} (${articlesList.length})`}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${indexOpen ? "rotate-180" : ""}`} />
            </button>
            {indexOpen && (
              <div className="max-h-[62vh] overflow-auto py-1" dir="rtl">
                {tocEntries.some((x) => x.guide || x.flag) && (
                  <p className="px-3 py-2 text-[11px] leading-relaxed text-slate-500 border-b" style={{ borderColor: "var(--line)" }}>
                    {tocEntries.some((x) => x.guide) && (isAr ? "عناوين المواد استرشادية للتصفح فقط وليست من نص القانون. " : "Article headings are navigation aids, not part of the law's text. ")}
                    {tocEntries.some((x) => x.flag) && (isAr ? "علامة «؟» تعني أن رقم المادة أُعيد ترتيبه آلياً ويحتاج تدقيقاً بالنص الأصلي." : "A “؟” mark means the article number was auto-repaired and should be checked against the source.")}
                  </p>
                )}
                {tocEntries.map((e, i) => {
                  if (e.type === "chapter") {
                    return (
                      <div
                        key={`c-${i}`}
                        data-testid={`law-outline-${i}`}
                        className={`px-3 py-2 text-xs border-b bg-[var(--beige)]/40 ${e.level === 0 ? "font-bold" : "font-semibold"}`}
                        style={{
                          borderColor: "var(--line)",
                          color: e.level <= 1 ? "var(--gold-deep)" : "var(--ink)",
                          paddingInlineStart: 12 + e.level * 12,
                        }}
                      >
                        {e.title}
                      </div>
                    );
                  }
                  const label = e.reliable || numberingReliable ? e.num : e.seq;
                  const active = flashSeq === e.seq;
                  return (
                    <button
                      key={`a-${e.seq}`}
                      type="button"
                      onClick={() => goToSeq(e.seq)}
                      data-testid={`law-index-chip-${e.seq}`}
                      title={`${isAr ? "المادة" : "Article"} ${label}${e.topic ? " — " + e.topic : ""}`}
                      className={`w-full flex items-start gap-2 px-3 py-2 text-xs border-b transition-colors ${
                        active ? "" : "hover:bg-[var(--beige)]"
                      }`}
                      style={{
                        borderColor: "var(--line)",
                        background: active ? "rgba(255,235,59,0.45)" : undefined,
                      }}
                    >
                      {/* topic on the RIGHT (start), article number on the LEFT (end) */}
                      <span className="min-w-0 flex-1 text-start text-slate-700 leading-snug line-clamp-2">
                        {e.topic || ""}
                      </span>
                      <span
                        className="shrink-0 font-bold whitespace-nowrap"
                        style={{ color: "var(--gold-deep)" }}
                      >
                        {isAr ? "مادة" : "Art."} {label}{e.flag ? " ؟" : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </aside>
        ) : (
          <div />
        )}

        <article
          data-testid="law-content"
          className="prose-luxe max-w-none"
          style={{ fontFamily: "Tajawal, Inter, system-ui, sans-serif" }}
        >
          {renderText(law.text, { assignIds: true })}
        </article>
      </div>

      {/* AMENDMENT COMPARISON (before vs after, from official source) */}
      {amendments.length > 0 && (
        <div id="amendments-compare" className="mt-16 scroll-mt-24" data-testid="law-compare-section">
          <div className="flex items-center justify-between border-t pt-8" style={{ borderColor: "var(--line)" }}>
            <div className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5" style={{ color: "var(--gold-deep)" }} />
              <h2 dir="rtl" className="font-display text-2xl lg:text-3xl" style={{ color: "var(--gold-deep)" }}>
                {t.compareTitle} <span className="text-slate-400 text-xl">({amendments.length})</span>
              </h2>
            </div>
            <button
              type="button"
              data-testid="toggle-compare-btn"
              onClick={() => setShowCompare((v) => !v)}
              className="text-sm underline underline-offset-2 text-slate-500 hover:text-slate-900"
            >
              {showCompare ? t.cmpHide : t.cmpShow}
            </button>
          </div>
          {showCompare && (
            <>
              <p dir="rtl" className="mt-2 mb-6 text-sm text-slate-500">{t.compareHint}</p>
              <div className="space-y-6">
                {amendments.map((a, i) => {
                  const d = a.has_after ? diffWords(a.before, a.after) : null;
                  return (
                  <div
                    key={i}
                    data-testid={`law-compare-item-${i}`}
                    className="border rounded-sm overflow-hidden"
                    style={{ borderColor: "var(--line)" }}
                  >
                    {(a.label || a.by_law) && (
                      <div
                        dir="rtl"
                        className="px-4 py-2.5 text-sm flex flex-wrap items-center gap-2 border-b"
                        style={{ borderColor: "var(--line)", background: "var(--beige)" }}
                      >
                        {a.label && (
                          <span className="font-bold" style={{ color: "var(--gold-deep)" }}>
                            {isAr ? "المادة/البند" : "Article"} {a.label}
                          </span>
                        )}
                        {a.by_law && (
                          <span className="text-slate-500">
                            — {isAr ? "بموجب القانون" : "by law"} {a.by_law}
                          </span>
                        )}
                      </div>
                    )}
                    {(summaries[a.key] || (summarizing && a.has_after)) && (
                      <div
                        dir="rtl"
                        data-testid={`law-compare-ai-${i}`}
                        className="px-4 py-2.5 text-[13px] flex items-start gap-2 border-b"
                        style={{ borderColor: "var(--line)", background: "rgba(201,169,97,0.09)" }}
                      >
                        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--gold-deep)" }} />
                        {summaries[a.key] ? (
                          <span className="text-slate-700">
                            <strong style={{ color: "var(--gold-deep)" }}>{t.aiChanged} </strong>
                            {summaries[a.key]}
                          </span>
                        ) : (
                          <span className="text-slate-400">{t.aiLoading}</span>
                        )}
                      </div>
                    )}
                    <div className="grid md:grid-cols-2">
                      <div
                        dir="rtl"
                        className="p-4 text-[14px] leading-[1.9] border-b md:border-b-0 md:border-l"
                        style={{ borderColor: "var(--line)", background: "rgba(185,28,28,0.05)" }}
                      >
                        <div className="text-xs font-bold mb-2" style={{ color: "#b91c1c" }}>
                          ⟵ {t.cmpBefore}
                        </div>
                        <div style={{ color: "#7f1d1d" }}>
                          {d ? renderDiff(d.aOut) : a.before}
                        </div>
                      </div>
                      <div
                        dir="rtl"
                        className="p-4 text-[14px] leading-[1.9]"
                        style={{ background: "rgba(5,150,105,0.06)" }}
                      >
                        <div className="text-xs font-bold mb-2" style={{ color: "#047857" }}>
                          {t.cmpAfter} ⟶
                        </div>
                        <div style={{ color: a.has_after ? "#065f46" : "#94a3b8" }}>
                          {d ? renderDiff(d.bOut) : t.cmpCurrent}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* EXPLANATORY MEMORANDUM */}
      {hasMemo && (
        <div id="explanatory-memorandum" className="mt-16 scroll-mt-24" data-testid="law-memo-section">
          <div className="flex items-center justify-between border-t pt-8" style={{ borderColor: "var(--line)" }}>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" style={{ color: "var(--gold-deep)" }} />
              <h2 dir="rtl" className="font-display text-2xl lg:text-3xl" style={{ color: "var(--gold-deep)" }}>
                {t.memoTitle}
              </h2>
            </div>
            <button
              type="button"
              data-testid="toggle-memo-btn"
              onClick={() => setShowMemo((v) => !v)}
              className="text-sm underline underline-offset-2 text-slate-500 hover:text-slate-900"
            >
              {showMemo ? t.memoHide : t.memoShow}
            </button>
          </div>
          {showMemo && (
            <>
              <p dir="rtl" className="mt-2 mb-6 text-sm text-slate-500">
                {t.memoHint}
              </p>
              <article
                data-testid="law-memo-content"
                className="prose-luxe max-w-none"
                style={{ fontFamily: "Tajawal, Inter, system-ui, sans-serif" }}
              >
                {renderText(law.explanatory_memorandum, { assignIds: false })}
              </article>
            </>
          )}
        </div>
      )}

      {/* DISCLAIMER */}
      <div
        className="mt-16 pt-6 border-t text-xs text-slate-500 leading-relaxed"
        style={{ borderColor: "var(--line)" }}
      >
        ⚖️ {t.disclaimer}
      </div>
    </div>
  );
}
